

import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { staggerContainer, fadeInUp } from "@/lib/motion";
import { RefreshDouble, DesignPencil, DatabaseScript } from "iconoir-react";
import { useLanguage } from "@/components/LanguageContext";

export function ExpertiseSection() {
  const { t } = useLanguage();

  const poles = [
    {
      icon: RefreshDouble,
      title: t("AVISIUM Mobilité", "AVISIUM Mobility"),
      points: [
        t("Planification stratégique (PMUD, PMUS)", "Strategic Planning (SUMP)"),
        t("Schémas de transport en commun (TCSP, BHNS)", "Mass Transit Schemes (BRT, LRT)"),
        t("Plans de circulation et stationnement", "Circulation and Parking Plans"),
        t("Mobilité active et Zones à Faibles Émissions", "Active Mobility & Low-Emission Zones"),
        t("Logistique urbaine", "Urban Logistics"),
      ]
    },
    {
      icon: DesignPencil,
      title: t("AVISIUM Ingénierie", "AVISIUM Engineering"),
      points: [
        t("Études techniques de trafic et capacité", "Technical Traffic & Capacity Studies"),
        t("Photogrammétrie et modélisation 3D par drone", "Drone Photogrammetry & 3D Modeling"),
        t("Relevés topographiques", "Topographic Surveys"),
        t("Instrumentation et dispositifs terrain", "Custom Field Instrumentation Devices"),
      ]
    },
    {
      icon: DatabaseScript,
      title: t("AVISIUM Digital Solutions", "AVISIUM Digital Solutions"),
      points: [
        t("IA et Vision par ordinateur pour le trafic", "AI & Computer Vision for Traffic"),
        t("Plateformes et Dashboards analytiques", "Analytical Dashboards & Platforms"),
        t("Automatisation d'analyses (BI)", "Data Automation & Business Intelligence"),
        t("Intégration SIG et cartographie décisionnelle", "GIS Integration & Decision Mapping"),
      ]
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-charcoal relative overflow-hidden">
      {/* Deep immersive background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber/10 via-charcoal to-charcoal pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-blue-grey/10 via-transparent to-transparent pointer-events-none" />
      
      {/* Abstract Grid Mesh */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="expertise-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#FFFFFF" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#expertise-grid)" />
        </svg>
      </div>

      <Container className="relative z-10">
        <div className="text-center mb-16 lg:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full bg-white/5 border border-white/10 text-xs font-semibold tracking-[0.2em] uppercase text-warm-white backdrop-blur-md"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber animate-pulse" />
            {t("Notre Approche", "Our Approach")}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-display font-semibold text-warm-white leading-tight"
            dangerouslySetInnerHTML={{
              __html: t(
                "Trois pôles d'excellence <span class='text-amber italic'>synergiques</span>",
                "Three <span class='text-amber italic'>Synergistic</span> Pillars of Excellence"
              )
            }}
          />
        </div>

        <motion.div
           variants={staggerContainer}
           initial="hidden"
           whileInView="visible"
           viewport={{ once: true, margin: "-100px" }}
           className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {poles.map((pole, index) => (
             <motion.div
               key={index}
               variants={fadeInUp}
               className="group relative flex flex-col p-8 md:p-10 rounded-3xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-amber/40 transition-all duration-500 overflow-hidden shadow-2xl"
             >
                {/* Glow effect on hover */}
                <div className="absolute -inset-px rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-amber/20 via-transparent to-transparent pointer-events-none" />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 mb-8 flex items-center justify-center rounded-2xl bg-charcoal border border-white/10 text-amber group-hover:scale-110 group-hover:bg-amber group-hover:text-charcoal transition-all duration-500 shadow-lg">
                    <pole.icon width={32} height={32} strokeWidth={1.5} />
                  </div>
                  
                  <h3 className="text-2xl font-sans font-bold text-warm-white mb-6 group-hover:text-amber transition-colors duration-300">
                    {pole.title}
                  </h3>
                  
                  {/* Animated dividing line */}
                  <div className="h-px w-12 bg-amber/50 mb-8 group-hover:w-full transition-all duration-700 ease-out" />
                  
                  <ul className="flex flex-col gap-4">
                    {pole.points.map((point, idx) => (
                      <li 
                        key={idx} 
                        className="flex items-start gap-3 text-sm md:text-base text-warm-white/70 group-hover:text-warm-white/90 transition-colors duration-300"
                      >
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber/50 shrink-0 group-hover:bg-amber group-hover:scale-150 transition-all duration-300" />
                        <span className="leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
             </motion.div>
          ))}
        </motion.div>
      </Container>
    </section>
  );
}
